// #39 Bloque C — shared result-handling for the MOS optimized report endpoint
// (`POST mos/fabricationOrderReportQuantity/...`). The response body changed from
// `{message}` to `{solicitadas, reportadas, falloEn, motivo}` on every success/partial (HTTP 200,
// sun #3527/RN20): the endpoint now fails fast with a per-piece commit, so a batch can come back
// partially confirmed. `falloEn`/`motivo` travel as `null` literal on a full success (API
// boundary) — normalized here to `''`/no-op so callers never see `null` (CLAUDE.md).
//
// Reused by the 3 call sites that report OK/NOK quantities: ReportQuantityDialog,
// ActiveSingleJobContainer and ActiveMultiJobContainer (S37, S39, S19).

const RAW_MATERIAL_REFERENCE_CODE = 'RAW_MATERIAL_REFERENCE'
const SELECCION_MODE = 'SELECCION'

// Shared by hasPendingRawMaterialResources (S19) and getFirstRawMaterialReference (S16): the raw
// material reference resources planned for the currently active node, read from `materials`
// (already loaded by useJobData, no new call to the hot-path polling).
const getRawMaterialReferenceResources = (materials, node) => {
  const list = Array.isArray(materials) ? materials : []
  const nodeId = node?.id
  return list
    .filter((material) => material?.id === nodeId)
    .flatMap((material) => material?.jobReference?.resources || [])
    .filter((resource) => resource?.resourceType?.code === RAW_MATERIAL_REFERENCE_CODE)
}

// S37/S39: turns the raw API response into a business-friendly summary — no nulls, `pending`
// already computed so a retry only needs to resend what is missing (never the confirmed pieces).
export const summarizeReportResult = (result, requestedQuantity = 0) => {
  const solicitadas = result?.solicitadas ?? requestedQuantity
  const reportadas = result?.reportadas ?? requestedQuantity
  const motivo = result?.motivo ?? ''
  const pending = Math.max(solicitadas - reportadas, 0)
  return {
    solicitadas,
    reportadas,
    pending,
    motivo,
    isComplete: pending === 0
  }
}

// S19: the technology flags travel per job in the active-context (`jobs[].<flag>`). The current job
// is matched by fabricationOrder.id + jobReference.id against `activeJobs` (useMosInfo()),
// fail-secure default false (nothing automatic unless the backend explicitly says so). Every flag
// reader below is this same lookup — story 79 (S41) adds the third one, so it lives here once.
// Story 113 (S159/S160): `fallback` was added so the mode (a string, C4) can share this very lookup
// with the five boolean flags without forcing a `false` default on it — every existing caller keeps
// the `false` it always had.
const getJobFlag = (activeJobs, fabricationOrder, jobReference, flag, fallback = false) => {
  const list = Array.isArray(activeJobs) ? activeJobs : []
  const foId = fabricationOrder?.id
  const jrId = jobReference?.id
  const match = list.find((item) =>
    item?.fabricationOrder?.id === foId && item?.jobReference?.id === jrId)
  return match?.[flag] ?? fallback
}

// S19: drives the auto-consume of raw material when the piece is reported.
export const getJobAutoReportResources = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'autoReportResources')

// When it is on the parts are reported automatically, so the Active Jobs widgets no longer force the
// operator to report the pending quantity before finishing — the backend's `canFinish` still governs
// whether the job may actually be finished.
export const getJobAutoReportParts = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'autoReportParts')

// Story 79 / S41 (RN3, RN34): with the flag on the technology can materialize the raw material that
// is missing at report time, so the Components widget stops capping the typed quantity at the
// available stock. Resolved per job, never per device or per fabrication order.
export const getJobGenerateComponents = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'generateComponents')

// Story 86 / S51 (CA11/RN24/D18): a job whose lifecycle is governed by the acceptance form is
// observable but not operable from any Active Jobs widget (single/multi/compact legacy) -- Pause
// and Finish must degrade to disabled, exactly like the Planned Jobs actions cell (S48/S50). Same
// per-job lookup as the other flags above: absent/null on the matching job resolves to false
// (RN25, aditive/retrocompatible; the marks are per job, never per device).
export const getJobGovernedByAcceptance = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'governedByAcceptance')

// Story 100 (S75-S82, RN34): a JOB that crossed the external circuit gate (RN1 -- external, with a
// declared return deadline, not the MORTE night job) is observable on every MOS surface but not
// operable through the six standard actions (start, join, pause, finish, report, change operation)
// -- the widget built in Lote 1 is the only place that can dispatch/return it. Exactly the #86
// pattern above: never hidden, never filtered, never flagged as an error; additive, positive mark
// with a false default (POL5) so an older Sun, or a job outside the gate (RN1.1 undeclared return,
// RN1.2 the MORTE night job, S81/S82), behaves exactly as before this story.
//
// Decision-only helper (C9): row-shaped surfaces (Planned Jobs list/modal/assembly selector, all via
// useJobColumns) read the mark straight off the row with `isCircuitExternalJob`. The three
// active-job widgets (single/multi/legacy compact) do not share a render point, so each resolves it
// independently too, via the per-job lookup below (`getJobExternalCircuit`) -- there is no single
// place to gate from, only a single place to DECIDE.
export const isCircuitExternalJob = (job) => job?.externalCircuitJob === true

export const getJobExternalCircuit = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'externalCircuitJob')

// Story 113 (S159, C4/C5) — the mode of the open period of THIS machine, and the job's switch.
//
// `ACTIVE_PREPARATION` was already seeded by migration V1.404 (which also renamed `ACTIVE` ->
// `ACTIVE_EXECUTION`); until this story nothing ever wrote it, so every active job read as execution.
export const ACTIVE_PREPARATION_STATUS_CODE = 'ACTIVE_PREPARATION'

export const JOB_EXECUTION_MODE = {
  PREPARATION: 'PREPARATION',
  EXECUTION: 'EXECUTION'
}

/**
 * 🔴 THE RED RULE of the frozen contract (`03-dominio.md` §8.1, `contrato-sun.md` §7 deviation 3),
 * mandatory on BOTH ends: any active-type code that is not EXACTLY `ACTIVE_PREPARATION` is
 * EXECUTION.
 *
 * The status catalogue is customer-extensible — an installation can create its own active statuses
 * (a setup one, say) and those codes reach `statusCode` without Sun or Jupiter knowing them. So the
 * question is never "is this execution?" (which would infer preparation by elimination) but "is this
 * the one preparation code?". Sun already applies it; this is Jupiter replicating it literally.
 *
 * Inverting it would let an installation that never switched preparation on see a job classified as
 * preparation and left unable to report parts (breaks RN57.1). Covers null/absent alike.
 */
export const resolveJobExecutionMode = (statusCode) =>
  statusCode === ACTIVE_PREPARATION_STATUS_CODE
    ? JOB_EXECUTION_MODE.PREPARATION
    : JOB_EXECUTION_MODE.EXECUTION

// C4: the status code of the open period of this machine. Empty string when the job is not in the
// active context (CLAUDE.md: no nulls in business logic) — which `resolveJobExecutionMode` reads as
// execution, i.e. exactly as today.
export const getJobStatusCode = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'statusCode', '') ?? ''

// C5: whether this job manages preparation at all. Absent => false, so a job that never declared it
// behaves as it did before the story (RN61).
export const getJobManagePreparation = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'managePreparation')

// Story 120 (S169, §7.2 #14): the batch a job currently belongs to on THIS machine, read with the
// exact same per-job lookup as the other flags above (`getJobFlag`, untouched -- it already serves
// the five boolean flags plus `getJobStatusCode`/`getJobManagePreparation`, and changing it affects
// all of them at once). New export, own absence value: `''`, not `null` (CLAUDE.md), reusing the
// `fallback` parameter story 113 (S159/S160) added to `getJobFlag` for exactly this shape of reader.
// With the field absent (an older Sun, RN69/POL7), every surface reads no batch and shows no notice.
export const getJobBatchId = (activeJobs, fabricationOrder, jobReference) =>
  getJobFlag(activeJobs, fabricationOrder, jobReference, 'batchId', '') ?? ''

// The one predicate the four station widgets ask: is THIS job, on THIS machine, preparing?
export const isJobInPreparation = (activeJobs, fabricationOrder, jobReference) =>
  resolveJobExecutionMode(getJobStatusCode(activeJobs, fabricationOrder, jobReference)) ===
    JOB_EXECUTION_MODE.PREPARATION

// S19: "hay recursos pendientes de reportar" is derived from the materials already loaded by
// useJobData (getResourcesByWorkflowId), without adding a new call to the hot-path polling — same
// filter ResourceListWidget/JobTabs already apply (resourceType RAW_MATERIAL_REFERENCE for the
// currently selected node).
export const hasPendingRawMaterialResources = (materials, node) =>
  getRawMaterialReferenceResources(materials, node).length > 0

// S16: SELECCION requires the operator to pick which instances/loads to consume BEFORE the report
// is confirmed — only when auto-consume applies (flag on + Machine Storage present) and the mode
// persisted for the machine is SELECCION. Any other mode reports straight away, unchanged.
export const shouldOpenInstanceSelectionModal = (machineStorage, autoReportResources) =>
  Boolean(machineStorage?.present) &&
  machineStorage?.consumptionMode === SELECCION_MODE &&
  Boolean(autoReportResources)

// S16: the reference to pick instances from, reusing the same materials/node shape as
// hasPendingRawMaterialResources. When more than one raw material reference would need a
// selection, only the first is offered — a known scope limitation for this corte (mirrors the
// equivalent gap already accepted on the backend for a multi-piece SELECCION batch, plan-sun.md).
export const getFirstRawMaterialReference = (materials, node) =>
  getRawMaterialReferenceResources(materials, node)[0]?.resource ?? {}

export const buildReportSummaryMessage = (t_, summary) =>
  t_('{{reportadas}} of {{solicitadas}} pieces reported', {
    reportadas: summary.reportadas,
    solicitadas: summary.solicitadas
  })

export const buildPartialReportMessage = (t_, summary) => {
  const base = buildReportSummaryMessage(t_, summary)
  const reason = summary.motivo ? t_(summary.motivo) : ''
  return reason ? `${base} - ${reason}` : base
}

export const buildPendingResourcesNotice = (t_) =>
  t_('There are pending raw material resources to report manually')

// Single entry point for the 3 call sites: reads the response, decides the alert severity/message
// (S37 happy path, S39 partial) and — when auto-consume is off and there are pending raw material
// resources (S19) — appends the manual-path notice. `pending` is what a retry must send next
// (S39): never the already-confirmed pieces.
export const resolveReportOutcome = ({
  result,
  requestedQuantity = 0,
  autoReportResources = false,
  hasPendingResources = false,
  t_ = (key) => key
}) => {
  const summary = summarizeReportResult(result, requestedQuantity)
  const summaryMessage = summary.isComplete
    ? buildReportSummaryMessage(t_, summary)
    : buildPartialReportMessage(t_, summary)
  const needsManualResourcesNotice = !autoReportResources && hasPendingResources
  const message = needsManualResourcesNotice
    ? `${summaryMessage} ${buildPendingResourcesNotice(t_)}`
    : summaryMessage

  return {
    severity: summary.isComplete ? 'success' : 'warning',
    message,
    pending: summary.pending,
    isComplete: summary.isComplete
  }
}
